/**
 * Do not disturb ("Pause notifications"): a per-person setting on the server (contracts NotificationSettings,
 * GET/PUT /v1/account/notifications). Paused = a manual pause that hasn't run out, or inside the daily quiet-hours
 * schedule (in the person's time zone). While paused the API sends no pushes and clients play no sound and show no
 * banners; Activity and unread counts still update. A moon shows on my avatar.
 */
import type { DndSchedule, MemberDnd, NotificationSettings } from "@bundle/contracts";

const MIN = 60_000;
const DAY_MINUTES = 1440;

/** "Pause notifications" menu, in order. `tomorrow` = until 9:00 tomorrow (local); `custom` asks for a time. */
export const PAUSE_OPTIONS = ["30m", "1h", "2h", "tomorrow", "custom"] as const;
export type PauseOption = (typeof PAUSE_OPTIONS)[number];

/** Where "until tomorrow" ends (minutes after local midnight). */
export const TOMORROW_AT = 9 * 60;

/** A new schedule's defaults: every night 22:00–08:00. */
export const DEFAULT_SCHEDULE_DAYS: readonly number[] = [0, 1, 2, 3, 4, 5, 6];
export const DEFAULT_SCHEDULE_START = 22 * 60;
export const DEFAULT_SCHEDULE_END = 8 * 60;

/** Local wall-clock parts of an instant in a time zone (an unknown zone reads as UTC). */
export function zonedParts(now: number, timeZone: string): { day: number; minutes: number; seconds: number } {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", hour: "numeric", minute: "numeric", second: "numeric", hourCycle: "h23" }).formatToParts(now);
  } catch {
    parts = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short", hour: "numeric", minute: "numeric", second: "numeric", hourCycle: "h23" }).formatToParts(now);
  }
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "0";
  const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
  const hour = Number(get("hour")) % 24;
  return { day: day < 0 ? 0 : day, minutes: hour * 60 + Number(get("minute")), seconds: Number(get("second")) };
}

/**
 * Whether `now` falls inside the schedule. A window starts at `start` on each listed day and ends at `end` the same
 * day, or the next day when `end` <= `start` (overnight). No days, or start = end: never.
 */
export function inSchedule(now: number, schedule: DndSchedule): boolean {
  if (schedule.start === schedule.end || schedule.days.length === 0) return false;
  const { day, minutes } = zonedParts(now, schedule.timeZone);
  const days = new Set(schedule.days);
  if (schedule.end > schedule.start) return days.has(day) && minutes >= schedule.start && minutes < schedule.end;
  // Overnight: the evening part belongs to today's window, the morning part to yesterday's.
  if (minutes >= schedule.start) return days.has(day);
  if (minutes < schedule.end) return days.has((day + 6) % 7);
  return false;
}

/** Notifications are paused right now: a manual pause still running, or inside the schedule. */
export function isPaused(now: number, settings: NotificationSettings): boolean {
  if (settings.pausedUntil !== undefined && settings.pausedUntil > now) return true;
  return settings.schedule !== undefined && inSchedule(now, settings.schedule);
}

/**
 * A coworker's Do not disturb is on right now (their Member.dnd, a copy of their settings): a moon on their avatar and
 * "Notifications paused" on their card. No `dnd` ⇒ never.
 */
export function memberPaused(member: { dnd?: MemberDnd } | undefined, now: number): boolean {
  return member?.dnd !== undefined && isPaused(now, member.dnd);
}

/**
 * The next instant (ms, after `now`) at which `isPaused` may change: the manual pause running out, or the schedule's
 * next start or end on the local wall clock. Clients re-check then (the moon appears or goes away without waiting
 * for another redraw). Undefined = nothing pending (no pause ahead, no schedule).
 */
export function nextDndChange(now: number, settings: NotificationSettings): number | undefined {
  let next: number | undefined;
  const take = (t: number) => {
    if (t > now && (next === undefined || t < next)) next = t;
  };
  if (settings.pausedUntil !== undefined) take(settings.pausedUntil);
  const s = settings.schedule;
  if (s !== undefined && s.start !== s.end && s.days.length > 0) {
    take(nextLocalTime(now, s.start, s.timeZone));
    take(nextLocalTime(now, s.end, s.timeZone));
  }
  return next;
}

/** The soonest `nextDndChange` among people (agents never pause), for one timer that refreshes every moon. */
export function nextMembersDndChange(members: Record<string, { kind?: string; dnd?: MemberDnd }>, now: number): number | undefined {
  let next: number | undefined;
  for (const m of Object.values(members)) {
    if (m.dnd === undefined || m.kind === "agent") continue;
    const t = nextDndChange(now, m.dnd);
    if (t !== undefined && (next === undefined || t < next)) next = t;
  }
  return next;
}

/**
 * When the pause ends (ms), for "Paused until 3:30 PM": the later of the manual pause and the current schedule
 * window's end. Undefined when not paused.
 */
export function pauseEndsAt(now: number, settings: NotificationSettings): number | undefined {
  if (!isPaused(now, settings)) return undefined;
  let end = settings.pausedUntil !== undefined && settings.pausedUntil > now ? settings.pausedUntil : now;
  const s = settings.schedule;
  if (s !== undefined && inSchedule(now, s)) {
    const { minutes, seconds } = zonedParts(now, s.timeZone);
    const left = (s.end - minutes + DAY_MINUTES) % DAY_MINUTES || DAY_MINUTES;
    end = Math.max(end, now - (now % 1000) - seconds * 1000 + left * MIN);
  }
  return end;
}

/** The `pausedUntil` a menu choice sets (`custom` takes its time from the picker instead). */
export function pauseUntil(option: Exclude<PauseOption, "custom">, now: number, timeZone: string): number {
  switch (option) {
    case "30m":
      return now + 30 * MIN;
    case "1h":
      return now + 60 * MIN;
    case "2h":
      return now + 120 * MIN;
    case "tomorrow": {
      const { minutes, seconds } = zonedParts(now, timeZone);
      return now - (now % 1000) - seconds * 1000 + (DAY_MINUTES - minutes + TOMORROW_AT) * MIN;
    }
  }
}

/** The next time the wall clock in `timeZone` reads `minutes` after midnight ("Custom…: pause until 18:30"). */
export function nextLocalTime(now: number, minutes: number, timeZone: string): number {
  const cur = zonedParts(now, timeZone);
  const delta = (minutes - cur.minutes + DAY_MINUTES) % DAY_MINUTES || DAY_MINUTES;
  return now - (now % 1000) - cur.seconds * 1000 + delta * MIN;
}

/** Settings with the manual pause ended ("Resume notifications"); the schedule stays. */
export function resume(settings: NotificationSettings): NotificationSettings {
  return settings.schedule === undefined ? {} : { schedule: settings.schedule };
}

/** "HH:MM" (24 h) ↔ minutes after midnight. Invalid input → undefined. */
export function parseTime(text: string): number | undefined {
  const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (m === null) return undefined;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return undefined;
  return h * 60 + min;
}

export function formatTime(minutes: number): string {
  const m = ((minutes % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** A stored or received value (any JSON) → valid settings; invalid parts are dropped. */
export function parseNotificationSettings(raw: unknown): NotificationSettings {
  if (typeof raw !== "object" || raw === null) return {};
  const r = raw as { pausedUntil?: unknown; schedule?: unknown };
  const out: NotificationSettings = {};
  if (typeof r.pausedUntil === "number" && Number.isFinite(r.pausedUntil) && r.pausedUntil > 0) out.pausedUntil = Math.floor(r.pausedUntil);
  const s = r.schedule as Partial<DndSchedule> | null | undefined;
  if (typeof s === "object" && s !== null) {
    const minute = (v: unknown) => typeof v === "number" && Number.isInteger(v) && v >= 0 && v < DAY_MINUTES;
    const days = Array.isArray(s.days) ? [...new Set(s.days.filter((d): d is number => typeof d === "number" && Number.isInteger(d) && d >= 0 && d <= 6))].toSorted((a, b) => a - b) : [];
    if (minute(s.start) && minute(s.end) && typeof s.timeZone === "string" && s.timeZone !== "") {
      out.schedule = { days, start: s.start as number, end: s.end as number, timeZone: s.timeZone };
    }
  }
  return out;
}
